import axiosInstance from '../lib/axios';
import { Interview, CreateInterviewRequest, UpdateInterviewRequest } from '../types/models';

class InterviewService {
  private baseUrl = '/interviews';

  // GET all interviews with filters
  async getInterviews(filters: Record<string, any> = {}): Promise<{ data: Interview[]; total: number; page: number; limit: number; hasMore: boolean }> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '' && value !== 'all') {
          const stringValue = typeof value === 'number' ? value.toString() : value;
          if (stringValue && stringValue.trim() !== '') {
            params.append(key, stringValue);
          }
        }
      });
      const queryString = params.toString();
      const url = `${this.baseUrl}${queryString ? `?${queryString}` : ''}`;
      const response = await axiosInstance.get(url);
      if (response.data && Array.isArray(response.data.data)) {
        return response.data;
      } else if (Array.isArray(response.data)) {
        // fallback if API returns array directly
        return { data: response.data, total: response.data.length, page: 1, limit: response.data.length, hasMore: false };
      } else {
        throw new Error('Unexpected response structure for getInterviews');
      }
    } catch (error) {
      throw error;
    }
  }

  // GET interview by ID
  async getInterviewById(id: string): Promise<Interview> {
    try {


      const response = await axiosInstance.get(`${this.baseUrl}/${id}`);



      // Handle different response formats
      let interviewData: Interview;

      if (response.data && response.data.data) {
        // Format: { data: { interview } }
        interviewData = response.data.data;
      } else if (response.data && typeof response.data === 'object' && '_id' in response.data) {
        // Format: { interview directly }
        interviewData = response.data as Interview;
      } else {
        console.error('Unexpected response format:', response.data);
        throw new Error('Interview not found');
      }


      return interviewData;
    } catch (error) {
      console.error('=== DEBUG: getInterviewById Error ===');
      console.error('Error fetching interview with ID:', id);
      console.error('Error details:', error);

      // If it's an axios error, log the response
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { data: any; status?: number } };
        console.error('Response status:', axiosError.response?.status);
        console.error('Response data:', axiosError.response?.data);
      }

      throw error;
    }
  }

  // POST create new interview
  async createInterview(data: CreateInterviewRequest): Promise<Interview> {
    try {
      const response = await axiosInstance.post(this.baseUrl, data);

      // Handle different response formats
      if (response.data && response.data.data) {
        // Format: { data: { interview } }
        return response.data.data;
      } else if (response.data && typeof response.data === 'object' && '_id' in response.data) {
        // Format: { interview directly }
        return response.data as Interview;
      } else {
        console.error('Unexpected response format after create:', response.data);
        throw new Error('Failed to create interview: missing data in response');
      }
    } catch (error: any) {
      if (error.response) {
        if (error.response.data) {
          throw error;
        }
      }
      throw error;
    }
  }

  // PATCH update interview
  async updateInterview(id: string, data: UpdateInterviewRequest): Promise<Interview> {
    try {
      // Validate status transition if status is being updated
      if (data.status) {
        const currentInterview = await this.getInterviewById(id);
        if (currentInterview) {
          const validationError = this.validateInterviewStatusTransition(currentInterview, data.status);
          if (validationError) {
            throw new Error(validationError);
          }
        }
      }

      // Create a clean copy of the data to ensure no mutations
      const cleanData = {
        interviewDate: data.interviewDate,
        interviewTime: data.interviewTime,
        candidate: data.candidate,
        department: data.department,
        interviewers: data.interviewers,
        interviewType: data.interviewType,
        location: data.location,
        notes: data.notes,
        status: data.status,
      };



      const response = await axiosInstance.patch(`${this.baseUrl}/${id}`, cleanData);

      // Handle different response formats
      let updatedInterview: Interview;

      if (response.data && response.data.data) {
        // Format: { data: { interview } }
        updatedInterview = response.data.data;
      } else if (response.data && typeof response.data === 'object' && '_id' in response.data) {
        // Format: { interview directly }
        updatedInterview = response.data as Interview;
      } else {
        console.error('Unexpected response format after update:', response.data);
        // fallback: fetch updated interview by ID
        return await this.getInterviewById(id);
      }

      return updatedInterview;
    } catch (error) {

      console.error('Error updating interview:', error);
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { data: any; status?: number } };
        console.error('Response data:', axiosError.response?.data);
        console.error('Response status:', axiosError.response?.status);

        // Log the specific validation messages
        if (axiosError.response?.data?.message && Array.isArray(axiosError.response.data.message)) {
          console.error('=== VALIDATION ERRORS ===');
          axiosError.response.data.message.forEach((msg: string, index: number) => {
            console.error(`Validation error ${index + 1}:`, msg);
          });
        }
      }
      throw error;
    }
  }

  // DELETE interview
  async deleteInterview(id: string): Promise<void> {
    try {
      await axiosInstance.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      throw error;
    }
  }



  // Update interview status with automatic timeline management
  async updateInterviewStatus(id: string, status: string): Promise<Interview> {
    try {


      // Get current interview to validate status transition
      const currentInterview = await this.getInterviewById(id);
      if (!currentInterview) {
        console.error('Interview not found with ID:', id);
        throw new Error(`Interview not found with ID: ${id}`);
      }



      // Validate status transition
      const validationError = this.validateInterviewStatusTransition(currentInterview, status);
      if (validationError) {
        console.error('Status transition validation failed:', validationError);
        throw new Error(validationError);
      }



      // Use the regular update endpoint
      const response = await axiosInstance.patch(`${this.baseUrl}/${id}`, { status: status.toLowerCase() });

      // Handle different response formats
      let updatedInterview: Interview;

      if (response.data && response.data.data) {
        // Format: { data: { interview } }
        updatedInterview = response.data.data;
      } else if (response.data && typeof response.data === 'object' && '_id' in response.data) {
        // Format: { interview directly }
        updatedInterview = response.data as Interview;
      } else {
        console.error('Unexpected response format after update:', response.data);
        throw new Error('Failed to update interview status');
      }



      // Note: Timeline entries are now handled by the backend automatically
      // The backend should create timeline entries when the status is updated

      return updatedInterview;
    } catch (error) {
      console.error('=== DEBUG: updateInterviewStatus Error ===');
      console.error('Error details:', error);

      // If it's an axios error, log the response
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { data: any; status?: number } };
        console.error('Response status:', axiosError.response?.status);
        console.error('Response data:', axiosError.response?.data);
      }

      throw error;
    }
  }

  // Validate interview status transitions based on business rules
  private validateInterviewStatusTransition(_interview: Interview, _newStatus: string): string | null {
    // Conditions removed as per user request for seamless flow
    return null; // No validation error
  }
}

export const interviewService = new InterviewService(); 